import { graphql } from 'gatsby';
import React from 'react';
import { css } from '@emotion/react';
import stripTags from 'striptags';
import { colors } from '../../primitives/colors';
import ArticleCard from '../../resources/article-card';
import { StyledButtonAsLink } from '../../primitives/styled-button';
import Link from '../../primitives/link';
import AccordionSet from '../faqs/accordion-set';
import Image from '../../primitives/image';
import Container from '../../primitives/grid/container';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import { breakpoints } from '../../primitives/tokens';
import { FaqQuestion } from '../faqs/types/faqs-types';
import Typography from '../../primitives/typography';
import Divider from '../../primitives/divider';
import Col from '../../primitives/grid/col';
import Row from '../../primitives/grid/row';
import RowSet from '../../primitives/grid/row-set';

const styles = {
    innerJsxWrap: css({
        display: 'flex',
        marginTop: '12px',
        justifyContent: 'flex-start',
    }),
    buttonLinkTitle: css({
        display: 'flex',
        justifyContent: 'center',
        [breakpoints.tb]: {
            justifyContent: 'flex-start',
        },
    }),
    bulletsSection: (isDarkMode: boolean) => css({
        color: isDarkMode ? colors.shadesWhite : colors.shadesBlack,
        backgroundColor: isDarkMode ? colors.shadesBlack : undefined,
    }),
    contentWrap: (isOneColumn: boolean) => css({
        display: 'flex',
        flexDirection: isOneColumn ? 'column' : 'row',
        justifyContent: isOneColumn ? 'stretch' : 'space-between',
        [breakpoints.tb]: {
            flexDirection: 'column',
            justifyContent: 'stretch',
        },
    }),
    headingWrap: (isOneColumn: boolean) => css({
        width: '100%',
        maxWidth: isOneColumn ? '448px' : '100%',
        marginRight: isOneColumn ? '80px' : '0',
        [breakpoints.tb]: {
            marginBottom: '32px',
            marginRight: '0',
        },
    }),
    copy: (isDarkMode: boolean) => css({
        marginTop: isDarkMode ? '28px' : undefined,
        whiteSpace: isDarkMode ? undefined : 'pre-line',
        fontSize: isDarkMode ? '20px' : '16px',
        lineHeight: isDarkMode ? '24px' : '28px',
        [breakpoints.tb]: {
            fontSize: '20px',
            lineHeight: '28px',
        },
    }),
    blurb: (isOneColumn: boolean, iconIsOnLeft: boolean) => css({
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: '32px',

        flexDirection: (() => {
            if (isOneColumn) {
                return 'row';
            }
            if (!iconIsOnLeft) {
                return 'column';
            }
            return 'row';
        })(),
        [breakpoints.tb]: {
            flexDirection: (() => {
                if (isOneColumn) {
                    return 'column';
                }
                if (iconIsOnLeft) {
                    return 'row';
                }
                return 'row';
            })(),
        },
    }),
    blurbHeading: (oneColumn: boolean) => css({
        marginBottom: oneColumn ? '12px' : '0',
        fontSize: '18px',
        lineHeight: '28px',
        fontWeight: 600,
    }),
    blurbContent: (isDarkMode: boolean) => css({
        letterSpacing: '0.29px',
        lineHeight: '28px',
        marginBottom: 'auto',
        fontSize: isDarkMode ? '20px' : '16px',
        maxWidth: 'max(430px, 90%)',
        [breakpoints.tb]: {
            lineHeight: '24px',
            maxWidth: 'unset',
            fontSize: !isDarkMode ? '20px' : '18px',
        },
    }),
    iconWrap: (isOneColumn: boolean, iconIsOnLeft: boolean) => css({
        width: '100%',
        maxWidth: '64px',
        position: 'relative',
        marginBottom: '8px',
        flexShrink: 0,
        marginRight: '12px',
        [breakpoints.tb]: {
            marginBottom: '0',
            top: !isOneColumn ? 0 : '-8px',
        },
        [breakpoints.mb]: {
            maxWidth: '40px',
            marginRight: iconIsOnLeft ? '20px' : undefined,
        },
    }),
};

const IconBullets: React.FC<Queries.IconBulletsDataFragment> = (data) => {
    const {
        headline,
        subheadline,
        copy,
        endCopy,
        blurbs,
        settings,
        buttonLink,
    } = data;

    if (!settings) return null;

    const { darkMode, oneColumn, numberOfGridColumns, style } = settings;

    const gridColumnSize = (() => {
        const baseGridColumns = numberOfGridColumns || 1;
        if (oneColumn) {
            if (baseGridColumns % 2 === 0) {
                return Math.max(12 / baseGridColumns, 1);
            }
            return 4;
        }
        return Math.max(12 / baseGridColumns, 1);
    })();

    return (
        <section css={[{
            padding: '48px 0 0',
            overflow: 'hidden',
            background: darkMode ? colors.shadesBlack : undefined,
            color: darkMode ? colors.shadesWhite : colors.shadesBlack,
        }]}
        >
            <Container css={{
                position: 'relative',
                paddingTop: '4px',
            }}
            >
                {(headline || subheadline || copy) && (
                    <div css={{ marginBottom: '32px' }}>
                        <RowSet breakpoints={{
                            dt: { between: 16 },
                        }}
                        >
                            {headline && (
                                <Typography
                                    as="h2"
                                    fontWeight="regular"
                                    reducedLetterSpacing
                                    fontSize="3xl"
                                    css={[{
                                        color: darkMode ? colors.primaryOrange : colors.shadesBlack,
                                        [breakpoints.tb]: {
                                            textAlign: 'left',
                                            maxWidth: '80%',
                                        },
                                    }]}
                                    dangerouslySetInnerHTML={{
                                        __html: apCaseOnlyTitleTags(headline) || '',
                                    }}
                                />
                            )}
                            {!!subheadline && stripTags(subheadline || '') && (
                                <Typography
                                    fontSize="xl"
                                    css={[RichTextEditorStyles, {
                                        color: darkMode ? colors.shadesWhite : undefined,
                                        marginTop: '4px',
                                        lineHeight: '28px',
                                    }]}
                                    dangerouslySetInnerHTML={{
                                        __html: apCaseOnlyTitleTags(subheadline),
                                    }}
                                />
                            )}
                            {!!copy && stripTags(copy || '') && (
                                <div
                                    css={[RichTextEditorStyles, styles.copy(!!darkMode)]}
                                    /* eslint-disable-next-line react/no-danger */
                                    dangerouslySetInnerHTML={{ __html: copy || '' }}
                                />
                            )}
                        </RowSet>
                    </div>
                )}
            </Container>
            <Divider css={{
                opacity: darkMode ? 0.1 : 0.6,
            }}
            />
            <Container css={{
                paddingTop: '48px',
                paddingBottom: '48px',

                [breakpoints.tb]: {
                    paddingTop: '32px',
                    paddingBottom: '32px',
                },
            }}
            >
                {(style === 'default' || !style) && (
                    <Row>
                        {(blurbs || []).map((blurb) => {
                            if (!blurb) return null;
                            const {
                                heading,
                                content,
                                icon,
                                iconAsLink,
                                link,
                                linkAsText,
                                iconOnLeft,
                            } = blurb;

                            const blurbIcon = (() => {
                                if (icon?.url) {
                                    if (iconAsLink && link?.href) {
                                        return (
                                            <Link to={link?.href || ''} css={styles.iconWrap(!!oneColumn, !!iconOnLeft)}>
                                                <Image image={icon} alt={icon.title || ''} />
                                            </Link>
                                        );
                                    }
                                    return (
                                        <div css={styles.iconWrap(!!oneColumn, !!iconOnLeft)}>
                                            <Image image={icon} alt={icon.title || ''} />
                                        </div>
                                    );
                                }
                                return null;
                            })();

                            return (
                                <Col
                                    breakpoints={{
                                        dt: { span: gridColumnSize },
                                        tb: { span: 6 },
                                        mb: { span: 12 },
                                    }}
                                    key={heading}
                                >
                                    <div css={styles.blurb(!!oneColumn, !!iconOnLeft)}>
                                        {blurbIcon}
                                        <div>
                                            {heading && (
                                                <h3 css={styles.blurbHeading(!!oneColumn)}>
                                                    {apCaseOnlyTitleTags(heading)}
                                                </h3>
                                            )}
                                            <div
                                                css={[RichTextEditorStyles, styles.blurbContent(!!darkMode)]}
                                                /* eslint-disable-next-line react/no-danger */
                                                dangerouslySetInnerHTML={{ __html: content || '' }}
                                            />
                                            {link && link.href && !(iconAsLink && !!icon?.url) && (
                                                <div css={styles.innerJsxWrap}>
                                                    {linkAsText && (
                                                        <Link
                                                            css={{ color: colors.primaryOrange }}
                                                            to={link.href}
                                                        >
                                                            {link.title}
                                                        </Link>
                                                    )}
                                                    {!linkAsText && (
                                                        <StyledButtonAsLink insetColor={darkMode ? colors.shadesBlack : colors.shadesWhite} to={link.href}>
                                                            {link.title}
                                                        </StyledButtonAsLink>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                )}
                {style === 'cards' && (
                    <Row css={{
                        rowGap: '24px',
                        [breakpoints.mb]: {
                            rowGap: '16px',
                        },
                    }}
                    >
                        {(blurbs || []).map((blurb) => {
                            if (!blurb) return null;
                            const { heading, content, icon, link } = blurb;
                            if (!link || !heading || !link?.href) return null;
                            return (
                                <Col
                                    breakpoints={{
                                        dt: { span: gridColumnSize },
                                        tb: { span: 6 },
                                        mb: { span: 12 },
                                    }}
                                    key={heading}
                                >
                                    <ArticleCard
                                        key={heading}
                                        article={{
                                            title: heading,
                                            featuredImage: icon ?? undefined,
                                            content: content || '',
                                            url: link.href,
                                        }}
                                    />
                                </Col>
                            );
                        })}
                    </Row>
                )}
                {style === 'accordion' && (
                    <AccordionSet
                        faqs={{
                            settings: {
                                darkMode: !!darkMode,
                            },
                            questions: {
                                questions: (blurbs || []).map((blurb) => {
                                    if (!blurb) return undefined;
                                    const { heading, content, icon, link } = blurb;
                                    if (!link) return undefined;
                                    if (heading && content) {
                                        const item: FaqQuestion = {
                                            question: heading,
                                            answer: content,
                                            icon: icon as ContentStackImage,
                                            url: link?.href || undefined,
                                        };
                                        return item;
                                    }
                                    return undefined;
                                }).filter(x => !!x) as FaqQuestion[],
                            },
                        }}
                        onDark={!!darkMode}
                    />
                )}
                {endCopy && (
                    <div
                        css={[RichTextEditorStyles, styles.copy(!!darkMode)]}
                        /* eslint-disable-next-line react/no-danger */
                        dangerouslySetInnerHTML={{ __html: endCopy }}
                    />
                )}
                {buttonLink?.title && buttonLink?.href && (
                    <div css={styles.buttonLinkTitle}>
                        <StyledButtonAsLink insetColor={darkMode ? colors.shadesBlack : colors.shadesWhite} to={buttonLink.href}>{buttonLink.title}</StyledButtonAsLink>
                    </div>
                )}
            </Container>
        </section>
    );
};

export const query = graphql`
    fragment IconBulletsData on CsIconBullets {
        ... on CsIconBullets {
            headline
            subheadline
            copy
            endCopy
            spacing {
                topGutter
                bottomGutter
            }
            settings {
                darkMode
                oneColumn
                numberOfGridColumns
                style
            }
            blurbs {
                heading
                content
                icon {
                    filename
                    title
                    url
                    contentStackAsset(size: QTR_MAX_WIDTH)
                    description
                }
                iconOnLeft
                link {
                    href
                    title
                }
                iconAsLink
                linkAsText
            }
            buttonLink {
                href
                title
            }
        }
    }
`;

export default IconBullets;
